import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import Slider from "react-slick";
import {
  FaCalendarAlt,
  FaClock,
  FaTaxi,
  FaShieldAlt,
  FaUserTie,
  FaRupeeSign,
  FaPhoneAlt,
  FaPlus,
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
  FaRoute,
  FaArrowRight,
  FaPlane,
  FaCheckCircle,
  FaThumbsUp,
} from "react-icons/fa";
import axios from "axios";
import { trackAbandonedBooking } from "../utils/abandonedBookingTracker";

// Import background image
import cabBgImage from "../assets/images/cab-bg.png";
import banglor from "../assets//images/q.png";
import shimla from "../assets/images/s.jpeg";
import haridwar from "../assets/images/a.jpeg";
import delhi from "../assets/images/d.jpeg";

// Import experience images
import image1 from "../assets/images/1.jpeg";
import image2 from "../assets/images/2.jpeg";
import image3 from "../assets/images/3.jpeg";
import image4 from "../assets/images/4.jpeg";
//car images
import car1 from "../assets/images/car-1.jpeg";
import car2 from "../assets/images/car-2.jpeg";
//taxi images
import taxi from "../assets/images/taxi-1.jpeg";
import taxi2 from "../assets/images/taxi-2.jpeg";

// Import testimonial images
import testi1 from "../assets/images/test-1.jpeg";
import testi2 from "../assets/images/test-2.jpeg";
import testi3 from "../assets/images/test-3.jpeg";
// Import experience images
import image5 from "../assets/images/5.jpeg";
import image6 from "../assets/images/6.jpeg";
import image7 from "../assets/images/7.jpeg";
import image8 from "../assets/images/8.jpeg";

// Custom components
import LocationInput from "../components/LocationInput";
import HeroSection from "../components/HeroSection";
import TestimonialSection from "../components/TestimonialSection";
import BlogSection from "../components/BlogSection";
import MediaSection from "../components/MediaSection";

// Components and Redux actions
import { updateBookingForm } from "../features/booking/bookingSlice";
import { API_URL, FALLBACK_API_URL } from "../config/apiConfig";

// Use a fallback image URL directly since dynamic imports cause ESLint issues
const outstationTaxiImage =
  "https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80";

// Google Maps API is now handled by GoogleMapsProvider

// Known distances between major cities (in km)
const KNOWN_DISTANCES = {
  "rajkot-delhi": 1140,
  "delhi-rajkot": 1140,
  "mumbai-pune": 150,
  "pune-mumbai": 150,
  "bangalore-chennai": 350,
  "chennai-bangalore": 350,
  // Add more known routes as needed
};

// Dynamic data will be fetched from admin panel

// Normalize city name for consistent comparison
const normalizeCityName = (city) => {
  if (!city) return "";
  return city.toString().toLowerCase().trim();
};

// Get known distance between two cities if available
const getKnownDistance = (from, to) => {
  const fromNormalized = normalizeCityName(from);
  const toNormalized = normalizeCityName(to);
  const cacheKey = `${fromNormalized}-${toNormalized}`;
  return KNOWN_DISTANCES[cacheKey] || null;
};

const FALLBACK_AIRPORTS = [
  {
    code: "RAJ102",
    name: "Rajkot International Airport, Hirasar, Garida, Gujarat",
    mainCity: "Rajkot, Gujarat",
    state: "Gujarat",
    country: "India",
    latitude: 22.3818371,
    longitude: 71.03169439999999,
    serviceCities: [
      { cityName: "Rajkot, Gujarat" }
    ],
    active: true
  },
  {
    code: "RAJ",
    name: "Rajkot Airport",
    mainCity: "Rajkot",
    state: "Gujarat",
    country: "India",
    latitude: 22.3092,
    longitude: 70.7795,
    serviceCities: [
      { cityName: "Rajkot" },
      { cityName: "Jamnagar" },
      { cityName: "Bhavnagar" }
    ],
    active: true
  }
];

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get user from Redux store
  const { user } = useSelector((state) => state.auth);

  // Helper to load persisted search state from localStorage safely
  const storedSearch = (() => {
    try {
      const stored = localStorage.getItem("lastCabSearch");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Error reading lastCabSearch from localStorage:", e);
      return null;
    }
  })();

  // Form state
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [journeyType, setJourneyType] = useState(() => storedSearch?.journeyType || "outstation"); // outstation, local
  const [tripType, setTripType] = useState(() => storedSearch?.tripType || "oneWay"); // oneWay, roundTrip
  const [localServiceType, setLocalServiceType] = useState(() => storedSearch?.localServiceType || "rental"); // rental, airport
  const [multiCity, setMultiCity] = useState(() => storedSearch?.multiCity || false);

  // Local Rental specific state
  const [localCity, setLocalCity] = useState("");
  const [hourlyPackage, setHourlyPackage] = useState(() => storedSearch?.hourlyPackage || "2h/20km");

  // Airport Transfer specific state
  const [transferDirection, setTransferDirection] = useState(() => storedSearch?.transferDirection || "to"); // "to" or "from"
  const [selectedAirport, setSelectedAirport] = useState(() => storedSearch?.selectedAirport || "");
  const [airportCity, setAirportCity] = useState("");

  // Dynamic data from admin panel
  const [rentalPackages, setRentalPackages] = useState([]);
  const [airports, setAirports] = useState(FALLBACK_AIRPORTS);
  const [airportServiceCities, setAirportServiceCities] = useState([]);
  const [adminCities, setAdminCities] = useState([]);

  const [pickupDate, setPickupDate] = useState(() => {
    if (storedSearch?.pickupDate) {
      const parsedDate = new Date(storedSearch.pickupDate);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      if (!isNaN(parsedDate.getTime()) && parsedDate >= todayStart) {
        return parsedDate;
      }
    }
    return new Date(Date.now() + 30 * 60 * 1000);
  });
  // Get current time in 24-hour format (HH:MM)
  const getCurrentTime = useCallback(() => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const mins = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${mins}`;
  }, []);

  // Format time to 12-hour format with AM/PM
  const formatTo12Hour = useCallback((time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }, []);

  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = useCallback((time12) => {
    if (!time12) return "";
    const [time, period] = time12.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (period === "PM" && hours < 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }, []);

  // Convert 24-hour format to 12-hour format
  const convertTo12Hour = useCallback((time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  }, []);

  // Initialize pickup time with current time + 30 mins in 12-hour format or stored search
  const [pickupTime, setPickupTime] = useState(() => {
    if (storedSearch?.pickupTime && storedSearch?.pickupDate) {
      const parsedDate = new Date(storedSearch.pickupDate);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      if (!isNaN(parsedDate.getTime()) && parsedDate >= todayStart) {
        const isStoredDateToday =
          parsedDate.getDate() === todayStart.getDate() &&
          parsedDate.getMonth() === todayStart.getMonth() &&
          parsedDate.getFullYear() === todayStart.getFullYear();
        
        if (!isStoredDateToday) {
          return storedSearch.pickupTime;
        } else {
          // If stored date is today, check if stored time is in the future
          const now = new Date();
          const currentHours = now.getHours();
          const currentMinutes = now.getMinutes();
          
          const time12 = storedSearch.pickupTime;
          const [timePart, period] = time12.split(" ");
          if (timePart && period) {
            let [h, m] = timePart.split(":").map(Number);
            if (period === "PM" && h < 12) h += 12;
            else if (period === "AM" && h === 12) h = 0;
            
            if (h > currentHours || (h === currentHours && m >= currentMinutes)) {
              return time12;
            }
          }
        }
      }
    }
    const futureTime = new Date(Date.now() + 30 * 60 * 1000);
    let hours = futureTime.getHours();
    let minutes = futureTime.getMinutes();

    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  });

  const [isTimeUserSelected, setIsTimeUserSelected] = useState(false);

  // Handle scrolling to hash links on initial mount or path/hash change
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 300); // 300ms timeout ensures components have fully loaded and rendered
      }
    }
  }, [location]);

  // Periodically update pickup date and time if they have not been manually set by the user
  useEffect(() => {
    if (isTimeUserSelected) return;

    const updateDefaultTime = () => {
      const futureTime = new Date(Date.now() + 30 * 60 * 1000);
      
      // Update pickup date to current date + 30 mins
      setPickupDate(futureTime);

      // Update pickup time
      let hours = futureTime.getHours();
      let minutes = futureTime.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const formattedTime = `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
      
      setPickupTime(formattedTime);
      console.log("[PickupTime] Automatically updated default pickup time to:", formattedTime);
    };

    // Update immediately on mount / when effect runs
    updateDefaultTime();

    // Check and update every 30 seconds
    const interval = setInterval(updateDefaultTime, 30000);
    return () => clearInterval(interval);
  }, [isTimeUserSelected]);

  // Auto-submit form if there was a pending submit before login (handles reloads for corporate as well)
  useEffect(() => {
    const corporateToken = localStorage.getItem("corporateToken");
    const isLoggedIn = !!user || !!corporateToken;
    const pendingSubmit = localStorage.getItem("pendingSubmitAfterLogin") === "true";

    if (isLoggedIn && pendingSubmit) {
      console.log("🔄 User logged in, executing pending form submission...");
      localStorage.removeItem("pendingSubmitAfterLogin");
      handleSubmit({ preventDefault: () => {} });
    }
  }, [user]);

  const handlePickupDateChange = (date) => {
    setIsTimeUserSelected(true);
    setPickupDate(date);
  };

  const handlePickupTimeChangeWrapper = (time) => {
    setIsTimeUserSelected(true);
    setPickupTime(time);
  };
  const [returnDate, setReturnDate] = useState(() => {
    if (storedSearch?.returnDate) {
      const parsedDate = new Date(storedSearch.returnDate);
      if (!isNaN(parsedDate.getTime())) return parsedDate;
    }
    return new Date(new Date().setDate(new Date().getDate() + 1));
  });
  // Initialize return time with a sensible default in 12-hour format
  const [returnTime, setReturnTime] = useState(() => storedSearch?.returnTime || "10:00 AM");
  const [mobileNumber, setMobileNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Distance calculations for airport transfers
  const [pickupDistance, setPickupDistance] = useState(null);
  const [dropoffDistance, setDropoffDistance] = useState(null);

  // Additional cities for multi-city bookings
  const [additionalCities, setAdditionalCities] = useState(() => storedSearch?.additionalCities || []);
  const [cityCounter, setCityCounter] = useState(() => storedSearch?.cityCounter || 1);

  // Tab inputs memory to isolate addresses between Outstation, Airport, and Hourly Rental
  const tabInputsRef = useRef({
    outstation: {
      pickup: "",
      dropoff: "",
      pickupPlace: null,
      dropoffPlace: null
    },
    airport: {
      selectedAirport: "",
      airportCity: "",
      dropoff: "",
      dropoffPlace: null,
    },
    rental: {
      localCity: "",
      hourlyPackage: storedSearch?.journeyType === "local" && storedSearch?.localServiceType === "rental" ? storedSearch.hourlyPackage : "2h/20km"
    }
  });

  const prevTabRef = useRef(null);
  const stateRefs = useRef({});

  // Keep stateRefs updated with latest values on every render
  stateRefs.current = {
    pickup,
    dropoff,
    pickupPlace,
    dropoffPlace,
    selectedAirport,
    airportCity,
    localCity,
    hourlyPackage
  };

  useEffect(() => {
    const getTabKey = (jType, sType) => {
      if (jType === "outstation") return "outstation";
      if (jType === "local" && sType === "airport") return "airport";
      if (jType === "local" && sType === "rental") return "rental";
      return "outstation";
    };

    const prevTab = prevTabRef.current;
    const nextTab = getTabKey(journeyType, localServiceType);

    if (prevTab && prevTab !== nextTab) {
      const currentValues = stateRefs.current;

      // 1. Save current state to the PREVIOUS tab's storage in Ref
      if (prevTab === "outstation") {
        tabInputsRef.current.outstation = {
          pickup: currentValues.pickup,
          dropoff: currentValues.dropoff,
          pickupPlace: currentValues.pickupPlace,
          dropoffPlace: currentValues.dropoffPlace,
        };
      } else if (prevTab === "airport") {
        tabInputsRef.current.airport = {
          selectedAirport: currentValues.selectedAirport,
          airportCity: currentValues.airportCity,
          dropoff: currentValues.dropoff,
          dropoffPlace: currentValues.dropoffPlace,
        };
      } else if (prevTab === "rental") {
        tabInputsRef.current.rental = {
          localCity: currentValues.localCity,
          hourlyPackage: currentValues.hourlyPackage,
        };
      }

      // 2. Load state from the NEXT tab's storage in Ref and apply it
      if (nextTab === "outstation") {
        const data = tabInputsRef.current.outstation;
        setPickup(data.pickup);
        setDropoff(data.dropoff);
        setPickupPlace(data.pickupPlace);
        setDropoffPlace(data.dropoffPlace);
      } else if (nextTab === "airport") {
        const data = tabInputsRef.current.airport;
        setSelectedAirport(data.selectedAirport);
        setAirportCity(data.airportCity);
        setDropoff(data.dropoff);
        setDropoffPlace(data.dropoffPlace);
        setPickup(data.selectedAirport || "");
      } else if (nextTab === "rental") {
        const data = tabInputsRef.current.rental;
        setLocalCity(data.localCity);
        setHourlyPackage(data.hourlyPackage);
        setPickup("");
        setDropoff("");
        setPickupPlace(null);
        setDropoffPlace(null);
      }
    }

    prevTabRef.current = nextTab;
  }, [journeyType, localServiceType]);

  // API data
  const [popularCities, setPopularCities] = useState([]);
  const [popularRoutes, setPopularRoutes] = useState([
    {
      id: 1,
      from: "Bangalore",
      to: "Ooty",
      price: 3195,
      distance: 269,
      duration: 6,
      features: ["Scenic mountain route", "Comfortable AC cabs", "Experienced drivers", "24/7 customer support"]
    },
    {
      id: 2,
      from: "Bangalore",
      to: "Mysore",
      price: 1795,
      distance: 139,
      duration: 3,
      features: ["Historic palace tours", "Comfortable AC cabs", "Professional drivers", "24/7 customer support"]
    },
    {
      id: 3,
      from: "Bangalore",
      to: "Coorg",
      price: 2495,
      distance: 237,
      duration: 5,
      features: ["Coffee plantation visits", "Comfortable AC cabs", "Experienced drivers", "24/7 customer support"]
    },
    {
      id: 4,
      from: "Bangalore",
      to: "Chikmagalur",
      price: 2195,
      distance: 210,
      duration: 4,
      features: ["Hill station retreat", "Comfortable AC cabs", "Expert drivers", "24/7 customer support"]
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactHumanVerification, setContactHumanVerification] =
    useState(false);

  // Business form state
  const [businessForm, setBusinessForm] = useState({
    companyName: "",
    name: "",
    email: "",
    phone: "",
  });
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessHumanVerification, setBusinessHumanVerification] =
    useState(false);

  // Dynamic Route Section Images
  const [routeMainImage, setRouteMainImage] = useState(banglor);
  const [routeSmallImage, setRouteSmallImage] = useState(shimla);

  // Why Choose Us visibility on scroll
  const [showWhyChooseUs, setShowWhyChooseUs] = useState(false);

  // Slider settings for popular cities
  const citySettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 2560,
        settings: {
          slidesToShow: 6,
        },
      },
      {
        breakpoint: 2000,
        settings: {
          slidesToShow: 5,
        },
      },
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const images = [
    image1,
    image2,
    image3,
    image4,
    image5,
    image6,
    image7,
    image8,
  ];

  // Experience data with proper names and descriptions
  // Experience data - can be overridden by admin panel data
  const [experiencesList, setExperiencesList] = useState([
    {
      image: image1,
      title: "Mountain Escapes",
      description: "Scenic hill station getaways with breathtaking views",
    },
    {
      image: image2,
      title: "Beach Destinations",
      description: "Coastal paradise trips with sun, sand and sea",
    },
    {
      image: image3,
      title: "Heritage Tours",
      description: "Explore ancient monuments and cultural landmarks",
    },
    {
      image: image4,
      title: "City Exploration",
      description: "Discover vibrant urban attractions and nightlife",
    },
    {
      image: image5,
      title: "Wildlife Safari",
      description: "Adventure through national parks and sanctuaries",
    },
    {
      image: image6,
      title: "Spiritual Journeys",
      description: "Visit sacred temples and pilgrimage destinations",
    },
    {
      image: image7,
      title: "Desert Adventures",
      description: "Experience the charm of golden sand dunes",
    },
    {
      image: image8,
      title: "Valley Retreats",
      description: "Peaceful getaways in lush green valleys",
    },
  ]);

  const experiences = experiencesList;

  // Fetch dynamic data from admin panel
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        // Fetch rental packages
        try {
          console.log("🎁 Attempting to fetch rental packages...");
          const rentalResponse = await tryApiRequest(
            "/rental-package",
            null,
            "Fetching rental packages",
            "GET",
          );
          console.log("🎁 Rental packages API response:", rentalResponse?.data);
          if (rentalResponse?.data?.success) {
            console.log(
              "🎁 Setting rental packages:",
              rentalResponse.data.data,
            );
            console.log(
              "🎁 Number of packages:",
              rentalResponse.data.data.length,
            );
            setRentalPackages(rentalResponse.data.data);
          } else {
            console.log(
              "🎁 Rental packages API not successful:",
              rentalResponse?.data,
            );
          }
        } catch (error) {
          console.error("🎁 Failed to fetch rental packages:", error.message);
        }

        // Fetch airports
        try {
          const airportResponse = await tryApiRequest(
            "/airport",
            null,
            "Fetching airports",
            "GET",
          );
          if (airportResponse?.data?.success && airportResponse.data.data.length > 0) {
            console.log("🛫 Fetching airports success! Count:", airportResponse.data.count, "Data:", airportResponse.data.data);
            setAirports(airportResponse.data.data);
          } else {
            console.warn("🛫 Fetching airports empty or success false, keeping fallback airports");
          }
        } catch (error) {
          console.error("Failed to fetch airports:", error.message);
        }

        // Skip fetching cities - we get them from rental packages instead
        console.log(
          "🏙️ Skipping AdminCities fetch - using rental package cities instead",
        );
      } catch (error) {
        console.error("Error in fetchAdminData:", error);
      }
    };

    fetchAdminData();
  }, []);

  // Prefill mobile number if user is logged in
  useEffect(() => {
    // Check for user phone number - this runs on component mount and when Redux user changes
    const prefillMobileNumber = () => {
      console.log("Checking for user phone number to prefill");

      // Corporate staff session (first priority when present)
      try {
        const corporateToken = localStorage.getItem("corporateToken");
        const corporateMemberStr = localStorage.getItem("corporateMember");
        if (corporateToken && corporateMemberStr) {
          const corporateMember = JSON.parse(corporateMemberStr);
          const staffPhone = corporateMember?.mobileNumber;
          if (staffPhone) {
            console.log(
              "Setting mobile from corporateMember.mobileNumber:",
              staffPhone,
            );
            setMobileNumber(String(staffPhone));
            // Set corporate booking mode for navigation
            sessionStorage.setItem("corporateBookingMode", "1");
            console.log("✅ Corporate booking mode enabled");
            return true;
          }
        }
      } catch (e) {
        console.error("Error parsing corporateMember from localStorage:", e);
      }

      // First priority: Direct user object in localStorage - this is what authSlice.js uses
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);
          if (userData) {
            console.log("Found user data in localStorage:", userData);
            if (userData.phone) {
              console.log(
                "Setting mobile from localStorage user.phone:",
                userData.phone,
              );
              setMobileNumber(userData.phone);
              return true;
            } else if (userData.mobile) {
              console.log(
                "Setting mobile from localStorage user.mobile:",
                userData.mobile,
              );
              setMobileNumber(userData.mobile);
              return true;
            } else if (userData.user && userData.user.phone) {
              console.log(
                "Setting mobile from localStorage user.user.phone:",
                userData.user.phone,
              );
              setMobileNumber(userData.user.phone);
              return true;
            }
          }
        }
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }

      // Second priority: Redux store (should match localStorage in most cases)
      if (user) {
        console.log("Checking Redux user:", user);
        if (user.phone) {
          console.log("Setting mobile from Redux user.phone:", user.phone);
          setMobileNumber(user.phone);
          return true;
        } else if (user.mobile) {
          console.log("Setting mobile from Redux user.mobile:", user.mobile);
          setMobileNumber(user.mobile);
          return true;
        } else if (user.user && user.user.phone) {
          console.log(
            "Setting mobile from Redux user.user.phone:",
            user.user.phone,
          );
          setMobileNumber(user.user.phone);
          return true;
        }
      }

      // Last resort - userInfo in localStorage (not used by auth system but might be used elsewhere)
      try {
        const userInfoStr = localStorage.getItem("userInfo");
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          if (userInfo) {
            console.log("Found userInfo in localStorage:", userInfo);
            if (userInfo.phone) {
              console.log(
                "Setting mobile from localStorage userInfo.phone:",
                userInfo.phone,
              );
              setMobileNumber(userInfo.phone);
              return true;
            } else if (userInfo.mobile) {
              console.log(
                "Setting mobile from localStorage userInfo.mobile:",
                userInfo.mobile,
              );
              setMobileNumber(userInfo.mobile);
              return true;
            }
          }
        }
      } catch (e) {
        console.error("Error parsing userInfo from localStorage:", e);
      }

      console.log("No user phone number found in any storage location");
      return false;
    };

    // Run prefill on component mount
    prefillMobileNumber();
  }, [user]); // This effect will re-run when the Redux user changes

  // Fetch service cities when airport is selected
  useEffect(() => {
    const fetchServiceCities = async () => {
      if (selectedAirport) {
        try {
          const response = await tryApiRequest(
            `/airport/${selectedAirport}/cities`,
            null,
            `Fetching service cities for ${selectedAirport}`,
            "GET",
          );
          if (response?.data?.success) {
            setAirportServiceCities(response.data.data);
          }
        } catch (error) {
          console.error("Error fetching service cities:", error);
          setAirportServiceCities([]);
        }
      } else {
        setAirportServiceCities([]);
      }
    };

    fetchServiceCities();
  }, [selectedAirport]);

  // Handle URL parameters for auto-filling locations (from route redirects)
  useEffect(() => {
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");
    const tripParam = searchParams.get("trip");

    if (fromParam) {
      setPickup(fromParam);
      console.log("Auto-filled pickup location from URL:", fromParam);
    }

    if (toParam) {
      setDropoff(toParam);
      console.log("Auto-filled dropoff location from URL:", toParam);
    }

    if (tripParam === "oneWay") {
      setTripType("oneWay");
      setJourneyType("outstation");
      console.log("Set journey to outstation one-way from URL");
    }

    // Clear URL parameters after processing to clean up the URL
    if (fromParam || toParam || tripParam) {
      const newUrl = window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }
  }, [searchParams]);

  // Fetch data from backend
  useEffect(() => {
    const fetchPopularCities = async () => {
      setIsLoading(true);
      try {
        console.log(
          "Home: Fetching popular cities from:",
          `${API_URL}/popular-cities`,
        );
        const response = await axios.get(`${API_URL}/popular-cities`);
        console.log("Home: Popular cities response:", response.data);

        if (response.data && Array.isArray(response.data)) {
          setPopularCities(response.data);
        } else if (response.data && response.data.success) {
          setPopularCities(response.data.cities || []);
        }
      } catch (error) {
        console.error("Error fetching popular cities:", error);
        setPopularCities([]);
        // Don't show error to user for optional data
        console.warn(
          "Popular cities could not be loaded, continuing without them",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPopularCities();
  }, [API_URL]);

  // Fetch dynamic Top Experience images
  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'top_experience' }
        });
        if (response.data.success && response.data.data.length > 0) {
          // Map fetched images to our experience structure
          const fetchedExperiences = response.data.data.map((img, idx) => ({
            image: img.imageUrl,
            title: img.title || experiencesList[idx]?.title || `Destination ${idx + 1}`,
            description: img.description || experiencesList[idx]?.description || "Experience the best with CabZigo"
          }));

          // Merge with defaults if fewer than 8 fetched
          if (fetchedExperiences.length < 8) {
            const combined = [...fetchedExperiences, ...experiencesList.slice(fetchedExperiences.length)];
            setExperiencesList(combined);
          } else {
            setExperiencesList(fetchedExperiences);
          }
        }
      } catch (error) {
        console.error("Error fetching experiences:", error);
      }
    };
    fetchExperiences();
  }, [API_URL]);

  // Fetch dynamic Route Section images
  useEffect(() => {
    const fetchRouteImages = async () => {
      try {
        // Main Image
        const mainRes = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'route_section_main' }
        });
        if (mainRes.data.success && mainRes.data.data.length > 0) {
          setRouteMainImage(mainRes.data.data[0].imageUrl);
        }

        // Small Image
        const smallRes = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'route_section_small' }
        });
        if (smallRes.data.success && smallRes.data.data.length > 0) {
          setRouteSmallImage(smallRes.data.data[0].imageUrl);
        }
      } catch (error) {
        console.error("Error fetching route images:", error);
      }
    };
    fetchRouteImages();
  }, [API_URL]);

  // Scroll detection for Why Choose Us section visibility
  useEffect(() => {
    const handleScroll = () => {
      // Show Why Choose Us when user scrolls down more than 300px
      if (window.scrollY > 300) {
        setShowWhyChooseUs(true);
      } else {
        setShowWhyChooseUs(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Helper function to check if a date is today
  const isToday = useCallback((date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // Handle pickup time change
  const handlePickupTimeChange = (e) => {
    setIsTimeUserSelected(true);
    const newTime24 = e.target.value; // Input returns 24-hour format (e.g., "14:30")
    if (isToday(pickupDate)) {
      const currentTime = getCurrentTime();
      const [currentHours, currentMinutes] = currentTime.split(":").map(Number);
      const [newHours, newMinutes] = newTime24.split(":").map(Number);
      const currentTotalMinutes = currentHours * 60 + currentMinutes;
      const newTotalMinutes = newHours * 60 + newMinutes;
      if (newTotalMinutes < currentTotalMinutes) {
        const ampm = currentHours >= 12 ? "PM" : "AM";
        const displayHours = currentHours % 12 || 12;
        setPickupTime(
          `${displayHours}:${String(currentMinutes).padStart(2, "0")} ${ampm}`,
        );
        toast.warn("Pickup time cannot be earlier than the current time", {
          autoClose: 3000,
        });
        return;
      }
    }
    // Convert 24-hour format to 12-hour format before storing
    setPickupTime(convertTo12Hour(newTime24));
  };

  // Validate if a time is in the past for the current date
  const validateTime = useCallback(
    (date, time12) => {
      if (!time12) return false;

      // If not today, any time is valid
      if (!isToday(date)) return true;

      // Get current time in 24-hour format
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      // Convert selected time to 24-hour format for comparison
      const selectedTime24 = convertTo24Hour(time12);
      if (!selectedTime24) return false;

      const [selectedHours, selectedMinutes] = selectedTime24
        .split(":")
        .map(Number);

      // Compare hours and minutes
      if (selectedHours > currentHours) return true;
      if (selectedHours === currentHours) {
        return selectedMinutes >= currentMinutes;
      }
      return false;
    },
    [isToday, convertTo24Hour],
  );

  // Get the earliest allowed time for the time picker
  const getEarliestAllowedTime = useCallback(() => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();

    // Round up to next 5 minutes
    if (minutes % 5 !== 0) {
      minutes = minutes + (5 - (minutes % 5));
      if (minutes >= 60) {
        hours += 1;
        minutes = 0;
      }
    }

    // Handle midnight rollover
    if (hours >= 24) {
      hours = 0;
    }

    const displayHours = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  }, []);

  // Handle time change with validation
  const handleTimeChange = useCallback(
    (newTime, setTime) => {
      if (!newTime) return;

      // Always update the time first to ensure UI is responsive
      setTime(newTime);

      if (isToday(pickupDate)) {
        const isValid = validateTime(pickupDate, newTime);
        if (!isValid) {
          const earliestTime = getEarliestAllowedTime();
          // Use setTimeout to ensure state update happens after the current render cycle
          setTimeout(() => {
            setTime(earliestTime);
            toast.warn(
              "Selected time is in the past. Adjusted to the earliest available slot.",
            );
          }, 0);
        }
      }
    },
    [pickupDate, isToday, validateTime, getEarliestAllowedTime],
  );

  // Get Google Maps API status and methods from context
  const {
    isLoaded: isGoogleMapsReady,
    calculateDistance,
    loadError,
  } = useGoogleMaps();

  // Handle Google Maps load errors
  useEffect(() => {
    if (loadError) {
      console.error("Error loading Google Maps API:", loadError);
      toast.error("Error loading maps. Please refresh the page.");
    }
  }, [loadError]);

  // Calculate and display distance for airport transfers when location is selected
  const handleAirportLocationSelected = useCallback(async (place, direction) => {
    if (!place) return;

    try {
      // If place has coordinates, calculate distance from airport
      if (place.location && airportCity && selectedAirport && calculateDistance) {
        console.log(`📏 Calculating distance for ${direction} location`, {
          airport: airportCity,
          location: place.formatted_address || place.name,
          coords: place.location,
        });

        // Use airport city as origin and selected place as destination
        const distance = await calculateDistance(airportCity, place.formatted_address || place.name);

        if (distance) {
          console.log(`📏 Distance calculated for ${direction}:`, distance);
          if (direction === "pickup") {
            setPickupDistance(distance);
            toast.info(`📍 Distance from airport to pickup: ${distance} km`);
          } else if (direction === "dropoff") {
            setDropoffDistance(distance);
            toast.info(`📍 Distance from dropoff to airport: ${distance} km`);
          }
        }
      }
    } catch (error) {
      console.error(`Error calculating distance for ${direction}:`, error);
      // Don't show error to user, just log it
    }
  }, [airportCity, selectedAirport, calculateDistance]);

  const getCityLocation = (city) => {
    switch (city.toLowerCase()) {
      case 'bangalore':
        return { lat: 12.9716, lng: 77.5946 };
      case 'ooty':
        return { lat: 11.4102, lng: 76.6950 };
      case 'mysore':
        return { lat: 12.2958, lng: 76.6394 };
      case 'coorg':
        return { lat: 12.3375, lng: 75.8069 };
      case 'chikmagalur':
        return { lat: 13.3161, lng: 75.7720 };
      default:
        return { lat: 12.9716, lng: 77.5946 };
    }
  };

  const handleBookRoute = (route) => {
    setJourneyType("outstation");
    setTripType("oneWay");
    
    const fromAddr = route.from + ", Karnataka, India";
    const toAddr = route.to + (route.to.toLowerCase() === "ooty" ? ", Tamil Nadu, India" : ", Karnataka, India");
    
    setPickup(fromAddr);
    setPickupPlace({
      name: route.from,
      formatted_address: fromAddr,
      location: getCityLocation(route.from)
    });

    setDropoff(toAddr);
    setDropoffPlace({
      name: route.to,
      formatted_address: toAddr,
      location: getCityLocation(route.to)
    });

    // Scroll to top/booking section
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.success(`Selected route: ${route.from} to ${route.to}`);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🚀 Form submission started");
    console.log("📊 Current form state:", {
      journeyType,
      tripType,
      localServiceType,
      pickup,
      dropoff,
      localCity,
      hourlyPackage,
      selectedAirport,
      mobileNumber,
      errorMessage,
    });

    // Clear previous errors
    setErrorMessage("");

    // Validate pickup time is not in the past (for today's date)
    if (isToday(pickupDate) && pickupTime) {
      const now = new Date();
      const selectedTime24 = convertTo24Hour(pickupTime);
      if (selectedTime24) {
        const [selH, selM] = selectedTime24.split(":").map(Number);
        const currentTotalMins = now.getHours() * 60 + now.getMinutes();
        const selectedTotalMins = selH * 60 + selM;
        if (selectedTotalMins < currentTotalMins) {
          toast.error("⏰ Pickup time cannot be in the past. Please select a future time for today.", { autoClose: 4000 });
          return;
        }
      }
    }

    // Validate return date and time for round trips
    if (journeyType === "outstation" && tripType === "roundTrip" && returnDate && returnTime) {
      const pickupTime24 = convertTo24Hour(pickupTime);
      const returnTime24 = convertTo24Hour(returnTime);
      
      const getLocalDateString = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const pickupDateStr = getLocalDateString(pickupDate);
      const returnDateStr = getLocalDateString(returnDate);

      const pickupDateTime = new Date(`${pickupDateStr}T${pickupTime24}`);
      const returnDateTime = new Date(`${returnDateStr}T${returnTime24}`);
      
      const minReturnTime = new Date(pickupDateTime.getTime() + 60 * 60000); // pickup + 1 hour

      if (isNaN(pickupDateTime.getTime()) || isNaN(returnDateTime.getTime())) {
        toast.error("⏰ Invalid date or time format.");
        return;
      }

      if (returnDateTime < minReturnTime) {
        toast.error("⏰ Return time must be at least 1 hour after pickup time.", { autoClose: 4000 });
        return;
      }
    }

    // Basic validation based on journey type
    if (journeyType === "outstation") {
      console.log("✅ Validating outstation journey");
      if (!pickup || !dropoff) {
        console.log("❌ Missing pickup or dropoff locations");
        setErrorMessage("Please enter both pickup and drop-off locations");
        return;
      }
      // Check if pickup and dropoff are the same
      if (pickup.toLowerCase() === dropoff.toLowerCase()) {
        console.log("❌ Pickup and dropoff are the same");
        setErrorMessage("Pickup and drop-off locations cannot be the same");
        return;
      }
      console.log("✅ Outstation validation passed");
    } else if (journeyType === "local") {
      console.log("✅ Validating local journey");
      if (localServiceType === "rental") {
        console.log("✅ Validating rental service");
        console.log("📊 Rental validation data:", {
          localCity,
          hourlyPackage,
          rentalPackagesLength: rentalPackages.length,
          availableCities: rentalPackages
            .filter((pkg) => pkg.serviceCities && pkg.serviceCities.length > 0)
            .flatMap((pkg) =>
              pkg.serviceCities.map((sc) => sc.cityName || sc.name || sc),
            )
            .filter(Boolean),
        });

        if (!localCity) {
          console.log("❌ Missing local city, current value:", localCity);
          setErrorMessage("Please select a city for local rental");
          return;
        }
        if (!hourlyPackage) {
          console.log(
            "❌ Missing hourly package, current value:",
            hourlyPackage,
          );
          setErrorMessage("Please select an hourly package");
          return;
        }
        console.log("✅ Rental validation passed");
      } else if (localServiceType === "airport") {
        console.log("✅ Validating airport service");
        if (!selectedAirport) {
          console.log("❌ Missing selected airport");
          setErrorMessage("Please select an airport");
          return;
        }
        if (!dropoff) {
          console.log(`❌ Missing ${transferDirection === "to" ? "pickup" : "drop"} city for airport transfer`);
          setErrorMessage(transferDirection === "to" ? "Please select pickup city" : "Please select drop city");
          return;
        }
        console.log("✅ Airport validation passed");
      }
    }

    // Check if user is logged in (regular or corporate)
    const corporateToken = localStorage.getItem("corporateToken");
    const isCorporateLoggedIn = !!corporateToken;
    const isUserLoggedIn = !!user;

    // Resolve mobile number from session/stored data if not in state
    let finalMobileNumber = mobileNumber;

    if (!finalMobileNumber) {
      if (isCorporateLoggedIn) {
        try {
          const corporateMemberStr = localStorage.getItem("corporateMember");
          if (corporateMemberStr) {
            const corporateMember = JSON.parse(corporateMemberStr);
            finalMobileNumber = corporateMember?.mobileNumber || corporateMember?.phone;
          }
        } catch (e) {
          console.error("Error getting corporate phone:", e);
        }
      } else if (isUserLoggedIn) {
        finalMobileNumber = user.phone || user.mobile || (user?.user?.phone);
      }
    }

    console.log("✅ Validating mobile number:", finalMobileNumber, "Logged in:", isUserLoggedIn || isCorporateLoggedIn);

    // If we have no number and are NOT logged in, we definitely need it
    if (!finalMobileNumber && !isUserLoggedIn && !isCorporateLoggedIn) {
      console.log("❌ No mobile number and not logged in");
      setErrorMessage("Please enter a valid 10-digit mobile number");
      return;
    }

    // If we HAVE a number (from state or profile), validate its format
    if (finalMobileNumber && !/^[6-9]\d{9}$/.test(finalMobileNumber)) {
      console.log("❌ Invalid format for mobile number:", finalMobileNumber);
      setErrorMessage("Please enter a valid 10-digit mobile number");
      return;
    }

    // If still no number but logged in, we might need to ask, but usually this shouldn't happen
    // if we trust our prefill. Let's allow it for now if they are logged in.
    if (!finalMobileNumber && (isUserLoggedIn || isCorporateLoggedIn)) {
      console.log("⚠️ User is logged in but has no phone in profile");
      // Optionally set a guest phone or prompt, but let's try to proceed
    }

    console.log("✅ Mobile number validation passed, final:", finalMobileNumber);

    console.log("🚀 Starting post-validation processing...");

    // If we have place objects, use their coordinates for more accurate routing
    let fromLocation = pickup;
    let toLocation = dropoff;

    console.log("📍 Initial locations:", {
      fromLocation,
      toLocation,
      pickup,
      dropoff,
    });

    if (pickupPlace?.location) {
      fromLocation = `${pickupPlace.location.lat},${pickupPlace.location.lng}`;
      console.log("📍 Updated fromLocation with coordinates:", fromLocation);
    }

    if (dropoffPlace?.location) {
      toLocation = `${dropoffPlace.location.lat},${dropoffPlace.location.lng}`;
      console.log("📍 Updated toLocation with coordinates:", toLocation);
    }

    try {
      console.log("🔧 Setting loading state...");
      setIsLoading(true);

      // Calculate distance - skip for rental and airport services
      console.log("📏 Starting distance calculation...");
      let distance = 0;

      if (journeyType === "local") {
        console.log(
          "📏 Local service detected - skipping distance calculation",
        );
        // For local services (rental/airport), we don't need distance calculation
        distance = 0; // Set to 0 since it's not relevant for local services
      } else {
        // Only calculate distance for outstation journeys
        console.log("📏 Outstation journey - calculating distance");

        // First try to get known distance
        console.log("📏 Checking for known distance...");
        const normalizedFrom = normalizeCityName(pickup);
        const normalizedTo = normalizeCityName(dropoff);
        console.log("📏 Normalized cities:", { normalizedFrom, normalizedTo });
        const knownDistance = getKnownDistance(normalizedFrom, normalizedTo);

        if (knownDistance) {
          console.log("📏 Using known distance:", knownDistance);
          distance = knownDistance;
        } else if (calculateDistance) {
          console.log("📏 Attempting Google Maps distance calculation...");
          // If no known distance, try to calculate using Google Maps API
          try {
            const calculatedDistance = await calculateDistance(pickup, dropoff);
            if (calculatedDistance) {
              console.log(
                "📏 Google Maps distance calculated:",
                calculatedDistance,
              );
              distance = calculatedDistance;
            }
          } catch (error) {
            console.error("📏 Error calculating distance:", error);
            // Fallback to default distance if calculation fails
            distance = 50;
            console.log("📏 Using fallback distance:", distance);
          }
        } else {
          console.log(
            "📍 No distance calculation method available, using default",
          );
          // Fallback to default distance if no calculation method is available
          distance = 50;
        }
      }

      console.log("📏 Final distance:", distance);

      // Prepare booking data
      console.log("📝 Preparing booking data...");
      // Filter out empty cities from additionalCities
      const validAdditionalCities = additionalCities.filter(
        (city) => city && city.value && city.value.trim() !== ""
      );

      const bookingData = {
        pickup,
        dropoff,
        distance,
        journeyType,
        tripType,
        localServiceType: journeyType === "local" ? localServiceType : null,
        pickupDate: pickupDate.toISOString(),
        pickupTime: convertTo24Hour(pickupTime),
        mobileNumber: finalMobileNumber || mobileNumber,
        returnDate: tripType === "roundTrip" ? returnDate.toISOString() : null,
        returnTime:
          tripType === "roundTrip" ? convertTo24Hour(returnTime) : null,
        additionalCities: validAdditionalCities,
        pickupPlace,
        dropoffPlace,
        fromLocation,
        toLocation,
        // Local Rental specific fields
        localCity:
          journeyType === "local" && localServiceType === "rental"
            ? localCity
            : null,
        localCityName:
          journeyType === "local" && localServiceType === "rental"
            ? localCity // Use the city name directly since we're storing city names, not IDs
            : null,
        hourlyPackage:
          journeyType === "local" && localServiceType === "rental"
            ? hourlyPackage
            : null,
        hourlyPackageDetails:
          journeyType === "local" && localServiceType === "rental"
            ? rentalPackages.find((pkg) => pkg._id === hourlyPackage)
            : null,
        // Airport Transfer specific fields
        selectedAirport:
          journeyType === "local" && localServiceType === "airport"
            ? selectedAirport
            : null,
        selectedAirportDetails:
          journeyType === "local" && localServiceType === "airport"
            ? airports.find((airport) => airport.code === selectedAirport)
            : null,
        transferDirection:
          journeyType === "local" && localServiceType === "airport"
            ? transferDirection
            : null,
        airportCity:
          journeyType === "local" && localServiceType === "airport"
            ? airportCity
            : null,
      };

      console.log("Submitting booking with data:", bookingData);

      // Save search state to localStorage for persistence
      const searchState = {
        pickup,
        dropoff,
        pickupPlace,
        dropoffPlace,
        journeyType,
        tripType,
        localServiceType,
        multiCity: validAdditionalCities.length > 0,
        localCity,
        hourlyPackage,
        transferDirection,
        selectedAirport,
        airportCity,
        pickupDate: pickupDate.toISOString(),
        pickupTime,
        returnDate: returnDate.toISOString(),
        returnTime,
        additionalCities: validAdditionalCities,
        cityCounter,
      };
      localStorage.setItem("lastCabSearch", JSON.stringify(searchState));

      // Save to Redux store
      dispatch(updateBookingForm(bookingData));

      // Build query parameters based on journey type
      let queryParams = "";

      if (journeyType === "outstation") {
        queryParams = `?from=${encodeURIComponent(pickup)}&to=${encodeURIComponent(dropoff)}`;
        queryParams += `&fromLocation=${encodeURIComponent(fromLocation)}`;
        queryParams += `&toLocation=${encodeURIComponent(toLocation)}`;
        queryParams += `&distance=${distance}`;
        queryParams += `&journeyType=${journeyType}`;
        queryParams += `&tripType=${tripType}`;

        // Check for corporate booking - either from sessionStorage or direct corporate token check
        const isCorporateBooking = sessionStorage.getItem("corporateBookingMode") === "1" || !!localStorage.getItem("corporateToken");
        if (isCorporateBooking) {
          queryParams += `&corporate=1`;
          console.log("✅ Corporate booking parameter added to outstation journey");
        }

        queryParams += `&service=outstation`;

        if (validAdditionalCities.length > 0) {
          const viaCities = validAdditionalCities
            .map((city) => city.value)
            .join("|");
          queryParams += `&via=${encodeURIComponent(viaCities)}`;
        }
      } else if (journeyType === "local") {
        if (localServiceType === "rental") {
          const cityName = localCity; // Use city name directly
          const packageDetails = rentalPackages.find(
            (pkg) => pkg._id === hourlyPackage,
          );
          queryParams = `?service=rental&city=${encodeURIComponent(cityName)}&package=${encodeURIComponent(packageDetails?.name || hourlyPackage)}`;
        } else if (localServiceType === "airport") {
          const airportDetails = airports.find(
            (airport) => airport.code === selectedAirport,
          );
          const airportLocation = airportDetails
            ? airportDetails.name
            : selectedAirport;
          if (transferDirection === "to") {
            // For "To Airport": from=pickup city (stored in dropoff), to=airport
            const pickupCityName =
              airportServiceCities.find((city) => city._id === dropoff)?.name ||
              dropoff;
            queryParams = `?service=airport&from=${encodeURIComponent(pickupCityName)}&to=${encodeURIComponent(airportLocation)}&direction=to`;
            // Add distance if calculated
            if (pickupDistance) {
              queryParams += `&distance=${pickupDistance}`;
            }
          } else {
            // For "From Airport": from=airport, to=drop city (stored in dropoff)
            const dropCityName =
              airportServiceCities.find((city) => city._id === dropoff)?.name ||
              dropoff;
            queryParams = `?service=airport&from=${encodeURIComponent(airportLocation)}&to=${encodeURIComponent(dropCityName)}&direction=from`;
            // Add distance if calculated
            if (dropoffDistance) {
              queryParams += `&distance=${dropoffDistance}`;
            }
          }
          queryParams += `&airport=${encodeURIComponent(selectedAirport)}`;
        }

        // Check for corporate booking - either from sessionStorage or direct corporate token check
        const isCorporateBooking = sessionStorage.getItem("corporateBookingMode") === "1" || !!localStorage.getItem("corporateToken");
        if (isCorporateBooking) {
          queryParams += `&corporate=1`;
          console.log("✅ Corporate booking parameter added to local journey");
        }
      }

      queryParams += `&date=${pickupDate.toISOString()}`;
      queryParams += `&time=${encodeURIComponent(pickupTime)}`;

      // Add trip type to URL parameters
      queryParams += `&tripType=${tripType}`;

      if (journeyType === "outstation" && tripType === "roundTrip") {
        queryParams += `&returnDate=${returnDate.toISOString()}`;
        queryParams += `&returnTime=${encodeURIComponent(returnTime)}`;
      }

      // Track lead when user searches for cabs
      try {
        trackAbandonedBooking({
          isFindCabsClick: true,
          pickupLocation: { address: pickup || "" },
          dropLocation: { address: dropoff || "" },
          tripType: tripType || journeyType || "one-way",
          pickupDate: pickupDate ? pickupDate.toISOString() : "",
          pickupTime: pickupTime || "",
          returnDate: (journeyType === "outstation" && tripType === "roundTrip" && returnDate) ? returnDate.toISOString() : "",
          returnTime: (journeyType === "outstation" && tripType === "roundTrip") ? returnTime : "",
          exitPage: "/cabs",
        });
      } catch (trackErr) {
        console.warn("Lead tracking silent error:", trackErr);
      }

      // Navigate to cab selection with query parameters
      console.log("🚀 Navigating to /cabs with params:", queryParams);
      console.log("🚀 Full navigation URL:", `/cabs${queryParams}`);

      try {
        navigate(`/cabs${queryParams}`);
        console.log("✅ Navigation successful");
      } catch (navError) {
        console.error("❌ Navigation failed:", navError);
        toast.error("Navigation failed. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error processing booking:", error);
      toast.error("Failed to process booking. Please try again.");
    } finally {
      console.log("🏁 Form submission completed");
      setIsLoading(false);
    }
  };

  // Alias for form submission
  const handleFormSubmit = handleSubmit;

  // Helper function to try API endpoints with fallback
  const tryApiRequest = async (
    endpoint,
    data,
    description = "request",
    method = "POST",
  ) => {
    const endpoints = [
      `${API_URL}${endpoint}`,
      `${FALLBACK_API_URL}${endpoint}`,
    ];

    for (let i = 0; i < endpoints.length; i++) {
      const url = endpoints[i];
      console.log(`Trying ${description} - Attempt ${i + 1}:`, url);

      try {
        let response;
        const config = {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000, // 10 second timeout
        };

        if (method.toUpperCase() === "GET") {
          response = await axios.get(url, config);
        } else if (method.toUpperCase() === "POST") {
          response = await axios.post(url, data, config);
        } else if (method.toUpperCase() === "PUT") {
          response = await axios.put(url, data, config);
        } else if (method.toUpperCase() === "DELETE") {
          response = await axios.delete(url, config);
        }

        console.log(`${description} response from ${url}:`, response.data);
        return response;
      } catch (error) {
        console.error(
          `${description} failed at ${url}:`,
          error.message || error,
        );

        // If this is the last endpoint, throw the error
        if (i === endpoints.length - 1) {
          throw error;
        }

        // Otherwise, continue to next endpoint
        console.log(`Trying next endpoint...`);
      }
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      if (
        !contactForm.name ||
        !contactForm.email ||
        !contactForm.phone ||
        !contactForm.message
      ) {
        toast.error("All fields are required");
        return;
      }

      if (!contactHumanVerification) {
        toast.error("Please verify that you are human");
        return;
      }

      console.log("Contact form data:", contactForm);

      const response = await tryApiRequest(
        "/contact",
        contactForm,
        "Contact form submission",
      );

      if (response.data.success) {
        toast.success(
          "Message sent successfully! We will get back to you soon.",
        );
        setContactForm({ name: "", email: "", phone: "", message: "" });
        setContactHumanVerification(false);
      } else {
        toast.error(response.data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/contact`,
      });

      if (error.response?.status === 404) {
        toast.error(
          "API endpoint not found. Please check backend configuration.",
        );
      } else if (error.code === "ECONNABORTED") {
        toast.error("Request timeout. Please check your internet connection.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } finally {
      setContactLoading(false);
    }
  };

  // Handle business form submission
  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setBusinessLoading(true);

    try {
      if (
        !businessForm.companyName ||
        !businessForm.name ||
        !businessForm.email ||
        !businessForm.phone
      ) {
        toast.error("All fields are required");
        return;
      }

      if (!businessHumanVerification) {
        toast.error("Please verify that you are human");
        return;
      }

      console.log("Business form data:", businessForm);

      const response = await tryApiRequest(
        "/business-contact",
        businessForm,
        "Business form submission",
      );

      if (response.data.success) {
        toast.success(
          "Business inquiry sent successfully! We will contact you soon.",
        );
        setBusinessForm({ companyName: "", name: "", email: "", phone: "" });
        setBusinessHumanVerification(false);
      } else {
        toast.error(response.data.message || "Failed to send business inquiry");
      }
    } catch (error) {
      console.error("Business form error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/business-contact`,
      });

      if (error.response?.status === 404) {
        toast.error(
          "API endpoint not found. Please check backend configuration.",
        );
      } else if (error.code === "ECONNABORTED") {
        toast.error("Request timeout. Please check your internet connection.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send business inquiry. Please try again.");
      }
    } finally {
      setBusinessLoading(false);
    }
  };

  // Handle contact form input changes
  const handleContactChange = (e) => {
    setContactForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Handle business form input changes
  const handleBusinessChange = (e) => {
    setBusinessForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={i <= rating ? "text-yellow-500" : "text-gray-300"}
        >
          ★
        </span>,
      );
    }
    return stars;
  };

  return (
    <div className="overflow-x-hidden w-full pt-1.5 md:pt-0">
      {/* Hero Section with Booking Form */}
      <HeroSection
        journeyType={journeyType}
        setJourneyType={setJourneyType}
        tripType={tripType}
        setTripType={setTripType}
        localServiceType={localServiceType}
        setLocalServiceType={setLocalServiceType}
        pickup={pickup}
        setPickup={setPickup}
        dropoff={dropoff}
        setDropoff={setDropoff}
        pickupPlace={pickupPlace}
        setPickupPlace={setPickupPlace}
        dropoffPlace={dropoffPlace}
        setDropoffPlace={setDropoffPlace}
        localCity={localCity}
        setLocalCity={setLocalCity}
        hourlyPackage={hourlyPackage}
        setHourlyPackage={setHourlyPackage}
        selectedAirport={selectedAirport}
        setSelectedAirport={setSelectedAirport}
        transferDirection={transferDirection}
        setTransferDirection={setTransferDirection}
        airportCity={airportCity}
        setAirportCity={setAirportCity}
        airportServiceCities={airportServiceCities}
        pickupDate={pickupDate}
        setPickupDate={handlePickupDateChange}
        pickupTime={pickupTime}
        setPickupTime={handlePickupTimeChangeWrapper}
        returnDate={returnDate}
        setReturnDate={setReturnDate}
        returnTime={returnTime}
        setReturnTime={setReturnTime}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
        multiCity={multiCity}
        setMultiCity={setMultiCity}
        additionalCities={additionalCities}
        setAdditionalCities={setAdditionalCities}
        cityCounter={cityCounter}
        setCityCounter={setCityCounter}
        rentalPackages={rentalPackages}
        airports={airports}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        handleSubmit={handleSubmit}
        convertTo24Hour={convertTo24Hour}
        convertTo12Hour={convertTo12Hour}
        getCurrentTime={getCurrentTime}
        isToday={isToday}
        validateTime={validateTime}
        getEarliestAllowedTime={getEarliestAllowedTime}
        handlePickupTimeChange={handlePickupTimeChange}
        handleAirportLocationSelected={handleAirportLocationSelected}
      />

      {/* Brand Info Section */}
      <section className="py-6 md:py-16 bg-gray-50">
        <div className="max-w-[1180px] mx-auto px-4">

          <div className="bg-white rounded-xl shadow-lg">

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 
                      divide-y sm:divide-y-0 sm:divide-x divide-gray-300">

              {/* Verified Cabs */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-white">
                  <img
                    src="/icons/cab.png"
                    alt="Verified Cabs"
                    className="h-5 w-5 object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Verified Cabs</h3>
                  <p className="text-xs text-gray-600 mt-1">Fully verified vehicles & drivers</p>
                </div>
              </div>

              {/* Professional Drivers */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-white">
                  <img
                    src="/icons/driver.png"
                    alt="Professional Drivers"
                    className="h-5 w-5 object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Professional Drivers</h3>
                  <p className="text-xs text-gray-600 mt-1">Experienced & highly rated partners</p>
                </div>
              </div>

              {/* Safe Travel */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-white">
                  <img
                    src="/icons/Vector.png"
                    alt="Safe Travel"
                    className="h-5 w-5 object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Safe Travel</h3>
                  <p className="text-xs text-gray-600 mt-1">GPS tracked rides & 24/7 support</p>
                </div>
              </div>

              {/* Clear Pricing */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-white">
                  <img
                    src="/icons/svg.png"
                    alt="Clear Pricing"
                    className="h-5 w-5 object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Clear Pricing</h3>
                  <p className="text-xs text-gray-600 mt-1">No hidden charges, pay what you see</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* Popular Destinations */}
      <section className="bg-white py-8 md:py-20 overflow-hidden">
        <div className="max-w-[1180px] mx-auto px-4 lg:px-8">
          {/* Heading */}
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            <span className="text-blue-500">Cab</span>
            <span className="text-orange-500">Zigo Explore Top Experiences</span>
          </h2>

          <div className="w-64 h-2 bg-gradient-to-r from-gray-300 to-blue-500 rounded-full mx-auto mb-6 md:mb-16"></div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[250px] gap-6 overflow-hidden">
            {experiences && experiences.length > 0 ? (
              experiences.slice(0, 9).map((experience, index) => {
                // Define grid positioning for masonry layout
                let gridClasses = "relative rounded-xl overflow-hidden group";

                if (index === 0) {
                  // 1st - Tall Left (row-span-2)
                  gridClasses += " sm:row-span-2";
                } else if (index === 2) {
                  // 3rd - Wide (col-span-2) - only on sm screens
                  gridClasses += " sm:col-span-2";
                } else if (index === 4) {
                  // 5th - Tall (row-span-2) - only on sm screens
                  gridClasses += " sm:row-span-2";
                } else if (index === 6) {
                  // 7th - Wide (col-span-2) - only on sm screens
                  gridClasses += " sm:col-span-2";
                }

                return (
                  <div
                    key={index}
                    className={gridClasses}
                  >
                    <img
                      src={experience.image}
                      alt={experience.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-4">
                      <h3 className="text-white font-bold text-lg">{experience.title}</h3>
                      <p className="text-gray-300 text-sm">
                        {experience.description}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center text-gray-500">
                No experiences available
              </div>
            )}
          </div>
        </div>
      </section>


      {/* CabZigo Taxi Services */}
      <section
        className="py-0 bg-white relative overflow-visible -mx-4 sm:-mx-6 md:-mx-8 lg:-mx-12"
        id="services"
      >
        <div className="w-full relative">
          <div className="absolute inset-0 md:relative">
            <img
              src="https://cdn.builder.io/api/v1/image/assets%2Fe03af646243b4932a94c6107dc635444%2F42832dd1c4324196b2a5f6180b7e8b9c?format=webp&width=1920&height=2880"
              alt="CabZigo Taxi Services"
              className="w-full h-full md:h-auto object-cover object-center block"
            />
          </div>

          {/* Services Cards Grid - Overlaid on Image */}

          <div className="relative md:absolute md:bottom-[3%] left-0 right-0 py-8 md:py-0">
            <div className="max-w-[1180px] mx-auto px-4 sm:px-6 md:px-8 lg:px-6  ">

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 ml-4 md:ml-16">
                <span className="text-blue-500">Cab</span>
                <span className="text-orange-500">Zigo </span>
                <span className="text-black">Taxi Services</span>
              </h2>

              <div className="w-56 h-2 bg-gradient-to-r from-gray-300 to-blue-500 rounded-full mt-2 ml-4 md:ml-[120px] mb-5"></div>



              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">

                {/* Outstation Cabs Card */}
                <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Outstation Cabs
                      </h3>
                      <p className="text-gray-700 text-sm leading-relaxed mb-3">
                        Round-trip cab service with multiple stops, all sightseeing
                        included, no extra driver charges–simple, all-inclusive pricing.
                      </p>
                      <button
                        onClick={() => {
                          setJourneyType("outstation");
                          setTripType("roundTrip");
                          document
                            .getElementById("booking-form")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        }}
                        className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1.5 px-5 rounded-full text-sm transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <img
                            src="/icons/details-icon.png"
                            alt="details icon"
                            className="w-5 h-5 object-contain"
                          />
                          <span>Details</span>
                        </div>
                      </button>
                    </div>
                    <div className="flex-shrink-0">
                      <img
                        src="/icons/out.png"
                        alt="Location Icon"
                        className="w-20 h-20 object-contain"
                      />
                    </div>
                  </div>
                </div>

                {/* One Way Cab Card */}
                <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        One Way Cab
                      </h3>
                      <p className="text-gray-700 text-sm leading-relaxed mb-3">
                        Pay one-way fare only–no return charge. Fast, reliable one-way
                        cab service with all-inclusive pricing with no hidden fees.
                      </p>
                      <button
                        onClick={() => {
                          setJourneyType("outstation");
                          setTripType("oneWay");
                          document
                            .getElementById("booking-form")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        }}
                        className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1.5 px-5 rounded-full text-sm transition-colors"
                      >
                        <img
                          src="/icons/details-icon.png"
                          alt="details icon"
                          className="w-5 h-5 object-contain"
                        />
                        <span>Details</span>

                      </button>
                    </div>
                    <div className="flex-shrink-0">
                      <img
                        src="/icons/one.png"
                        alt="Location Icon"
                        className="w-20 h-20 object-contain"
                      />
                    </div>
                  </div>
                </div>

                {/* Local Car Rental Card */}
                <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Local Car Rental
                      </h3>
                      <p className="text-gray-700 text-sm leading-relaxed mb-3">
                        Flexible hourly or daily rentals–ideal for local sightseeing,
                        business travel, weddings, shopping, and outings.
                      </p>
                      <button
                        onClick={() => {
                          setJourneyType("local");
                          setLocalServiceType("rental");
                          document
                            .getElementById("booking-form")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        }}
                        className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1.5 px-5 rounded-full text-sm transition-colors"
                      >
                        <img
                          src="/icons/details-icon.png"
                          alt="details icon"
                          className="w-5 h-5 object-contain"
                        />
                        <span>Details</span>

                      </button>
                    </div>
                    <div className="flex-shrink-0">
                      <img
                        src="/icons/local.png"
                        alt="Location Icon"
                        className="w-20 h-20 object-contain"
                      />
                    </div>
                  </div>
                </div>

                {/* Airport Taxi Card */}
                <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Airport Taxi
                      </h3>
                      <p className="text-gray-700 text-sm leading-relaxed mb-3">
                        24/7 airport transfers to and from any location, with flight
                        delay monitoring, assured luggage space, and GPS tracking.
                      </p>
                      <button
                        onClick={() => {
                          setJourneyType("local");
                          setLocalServiceType("airport");
                          document
                            .getElementById("booking-form")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        }}
                        className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1.5 px-5 rounded-full text-sm transition-colors"
                      >
                        <img
                          src="/icons/details-icon.png"
                          alt="details icon"
                          className="w-5 h-5 object-contain"
                        />
                        <span>Details</span>
                      </button>
                    </div>
                    <div className="flex-shrink-0">
                      <img
                        src="/icons/rental.png"
                        alt="Location Icon"
                        className="w-20 h-20 object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Popular Cab Routes */}
      <section className="py-8 md:py-20 overflow-hidden" style={{ backgroundColor: "#F973161A" }} id="popular-routes">
        <div className="max-w-[1180px] mx-auto px-6">

          {/* Heading */}
          <div className="text-center mb-6 md:mb-14">
            <h2 className="text-3xl md:text-4xl font-bold">
              <span className="text-blue-600">Cab </span>
              <span className="text-black">Popular Cab Routes</span>
            </h2>

            <div className="w-64 h-2 bg-gradient-to-r from-gray-200 to-blue-600 rounded-full mx-auto mt-3"></div>
          </div>

          {/* Main Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">

            {/* LEFT SIDE IMAGES */}
            <div className="relative flex justify-center">

              {/* Big Image */}
              <img
                src={routeMainImage || banglor}
                alt="Main Cab"
                className="w-full max-w-[530px] h-[480px] object-cover rounded-lg mt-16"
              />

              {/* Small Overlapping Image */}
              <img
                src={routeSmallImage || shimla}
                alt="Small Cab"
                className="absolute top-[250px] right-[-25px]
           w-[210px] h-[160px]
           object-cover
           rounded-lg
           border-[2.5px] border-white
           shadow-lg"
              />

            </div>

            {/* RIGHT SIDE CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">

              {popularRoutes.map((route) => (
                <div
                  key={route.id}
                  className="relative bg-gray-100 rounded-lg p-6 shadow-md flex flex-col"
                >

                  {/* Price Ribbon */}
                  <div className="absolute top-0 right-0 w-[120px] h-[120px] overflow-hidden">
                    <div className="absolute top-[21px] right-[-37px]
                  w-[160px]
                  bg-gray-200
                  text-blue-600
                  font-bold
                  text-sm
                  text-center
                  py-1
                  rotate-45">
                      ₹{route.price}
                    </div>
                  </div>



                  <h3 className="text-lg font-bold text-gray-900">
                    {route.from} to {route.to}
                  </h3>

                  <p className="text-sm text-gray-600 mt-1">
                    {route.distance}km | {route.duration} hours
                  </p>

                  <ul className="mt-4 space-y-2 text-sm text-gray-700 flex-grow">
                    {route.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleBookRoute(route)}
                    className="mt-6 w-full bg-blue-600 text-white py-3 rounded-md flex items-center justify-center gap-2"
                  >
                    <img
                      src="/icons/details-icon.png"
                      alt="details icon"
                      className="w-5 h-5 object-contain"
                    />
                    <span>Book Now</span>
                  </button>

                </div>
              ))}

            </div>

          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section
        className="py-6 mt-6 md:mt-16"
        style={{
          backgroundColor: "#F5F7FA",
          gap: "40px"
        }}
      >
        <div className="max-w-[1180px] mx-auto px-4 sm:px-6 md:px-8 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h2
                className="text-gray-900 mb-4 text-3xl sm:text-4xl lg:text-[48px] font-semibold lg:leading-[58.67px]"
                style={{
                  fontFamily: "Inter",
                  letterSpacing: "0%"
                }}
              >
                Helping a local
                <br />
                <span className="text-blue-600">business reinvent itself</span>
              </h2>
              <p className="text-gray-600 text-lg">
                We reached here with our hard work and dedication
              </p>
            </div>

            {/* Right Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              <div className="flex items-start gap-4">
                <div className="text-blue-600 text-5xl flex-shrink-0"><img
                  src="/icons/Icon.png"
                  alt="Location Icon"
                  className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
                /></div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    2,245,341
                  </div>
                  <p className="text-gray-600 text-sm mt-1">Members</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-blue-600 text-5xl flex-shrink-0"><img
                  src="/icons/Icon1.png"
                  alt="Location Icon"
                  className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
                /></div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    46,328
                  </div>
                  <p className="text-gray-600 text-sm mt-1">Clubs</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-blue-600 text-5xl flex-shrink-0"><img
                  src="/icons/Icon2.png"
                  alt="Location Icon"
                  className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
                /></div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    828,867
                  </div>
                  <p className="text-gray-600 text-sm mt-1">Event Bookings</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-blue-600 text-5xl flex-shrink-0"><img
                  src="/icons/Icon3.png"
                  alt="Location Icon"
                  className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
                /></div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    1,926,436
                  </div>
                  <p className="text-gray-600 text-sm mt-1">Payments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialSection />

      {/* Media Section */}
      <MediaSection />
    </div>
  );
};

export default Home;
